import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Sparkle, TrendUp, ArrowRight, Clock, ChartLineUp, Storefront, MagnifyingGlass, X, Star,
} from "@phosphor-icons/react";
import { HERO_STATS, MARKETS, CRAVINGS, TEST_TRACKER, buildGenericDetail } from "./commerceData";
import OpportunityPanel from "./OpportunityPanel";
import RealClusterDetail from "./RealClusterDetail";
import QualityReview from "./QualityReview";
import ReviewWorkspace from "./ReviewWorkspace";
import PipelineHealth from "./PipelineHealth";
import PredictionValidation from "./PredictionValidation";
import FounderDiligence from "./FounderDiligence";
import DecisionBoard from "./DecisionBoard";
import ExistingSolutions from "./ExistingSolutions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scoreTone = (badge) =>
  badge === "Investigate" ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
  : badge === "Watch" ? "bg-amber-50 text-amber-700 ring-amber-200"
  : "bg-rose-50 text-rose-700 ring-rose-200";

const realBadge = (score) => (score >= 85 ? "Investigate" : score >= 75 ? "Watch" : "Avoid");

const ALL_MARKETS = "All Markets";

const dominantHigh = (dist) => {
  const d = dist || {};
  const h = d.high || 0;
  return h > 0 && h >= (d.medium || 0) && h >= (d.low || 0);
};

const isNewCluster = (c) => {
  if (!c.created_at) return false;
  const ms = Date.now() - new Date(c.created_at).getTime();
  return !isNaN(ms) && ms < 7 * 86400000;
};

const CLUSTER_FILTERS = [
  { id: "new_week", label: "🆕 New this week", test: isNewCluster },
  { id: "high_severity", label: "High Severity", test: (c) => dominantHigh(c.severity_dist) },
  { id: "high_intent", label: "High Buying Intent", test: (c) => dominantHigh(c.buying_intent_dist) },
  { id: "multi_source", label: "Multi-source", test: (c) => (c.source_count || 0) >= 2 },
  { id: "singleton", label: "Singleton", test: (c) => (c.member_count || 0) === 1 },
];

const SORT_OPTIONS = [
  { id: "score", label: "Score", key: (c) => c.craving_score || 0 },
  { id: "freq", label: "Frequency", key: (c) => c.member_count || 0 },
  { id: "sources", label: "Source Count", key: (c) => c.source_count || 0 },
  { id: "newest", label: "Newest", key: (c) => c.created_at || "" },
];

const SectionHead = ({ step, title, sub }) => (
  <div className="mb-6">
    <div className="text-[11px] font-black uppercase tracking-[0.22em] text-violet-600">{step}</div>
    <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-zinc-900">{title}</h2>
    {sub && <p className="mt-1 text-sm sm:text-base text-zinc-500">{sub}</p>}
  </div>
);

const MarketCard = ({ market, active, onClick }) => (
  <button onClick={onClick}
    className={`text-left rounded-2xl p-5 bg-white/75 backdrop-blur border transition-all hover:-translate-y-0.5 ${active ? "border-violet-400 ring-2 ring-violet-200 shadow-[0_20px_50px_-20px_rgba(139,92,246,0.5)]" : "border-violet-100 hover:border-violet-300 shadow-sm"}`}
    data-testid={`commerce-market-${market.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
    <div className="flex items-center gap-2.5 mb-2.5">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black text-white flex-shrink-0"
        style={{ background: "linear-gradient(135deg,#8b5cf6,#a855f7)" }}>{market.name[0]}</span>
      <h3 className="text-lg font-bold text-zinc-900">{market.name}</h3>
    </div>
    <p className="text-sm text-zinc-500">{market.cravings} cravings</p>
    <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-emerald-600">
      <TrendUp size={13} weight="bold" /> {market.momentum} momentum
    </div>
    <p className="mt-2 text-xs text-zinc-400">Top signal: <span className="text-zinc-600 font-medium">{market.signal}</span></p>
  </button>
);

const CravingCard = ({ craving, active, onClick }) => (
  <button onClick={onClick}
    className={`w-full text-left rounded-2xl p-4 bg-white/80 backdrop-blur border flex items-center justify-between gap-4 transition-all ${active ? "border-violet-400 ring-2 ring-violet-200" : "border-violet-100 hover:border-violet-300"}`}
    data-testid={`commerce-craving-${craving.id}`}>
    <div className="min-w-0">
      <h3 className="text-base font-bold text-zinc-900 truncate">{craving.title}</h3>
      <p className="text-xs text-zinc-500 mt-1">Freq: {craving.frequency} · Growth: {craving.growth} · Intent: {craving.intent} · Comp: {craving.competition}</p>
    </div>
    <div className="text-center flex-shrink-0">
      <div className="text-2xl font-black text-zinc-900 leading-none">{craving.score}</div>
      <div className={`mt-1 inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${scoreTone(craving.badge)}`}>{craving.badge}</div>
    </div>
  </button>
);

const RealCravingCard = ({ cluster, active, onClick, showMarket, shortlisted, onToggleShortlist }) => {
  const badge = realBadge(cluster.craving_score);
  return (
    <div onClick={onClick} role="button" tabIndex={0}
      className={`w-full text-left rounded-2xl p-4 bg-white/80 backdrop-blur border flex items-center justify-between gap-3 transition-all cursor-pointer ${active ? "border-violet-400 ring-2 ring-violet-200" : "border-violet-100 hover:border-violet-300"}`}
      data-testid={`commerce-cluster-${cluster.cluster_id}`}>
      <button
        onClick={(e) => { e.stopPropagation(); onToggleShortlist(cluster); }}
        title={shortlisted ? "Remove from shortlist" : "Add to shortlist"}
        aria-label="Toggle shortlist"
        className={`flex-shrink-0 transition-colors ${shortlisted ? "text-amber-400" : "text-zinc-300 hover:text-amber-400"}`}
        data-testid={`commerce-shortlist-btn-${cluster.cluster_id}`}>
        <Star size={20} weight={shortlisted ? "fill" : "regular"} />
      </button>
      <div className="min-w-0 flex-1">
        {showMarket && (
          <span className="inline-block mb-1 text-[10px] font-bold uppercase tracking-wide text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{cluster.market}</span>
        )}
        <h3 className="text-sm font-bold text-zinc-900 line-clamp-2">
          {isNewCluster(cluster) && (
            <span data-testid={`cluster-new-${cluster.cluster_id}`} className="inline-block align-middle mr-1.5 text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">New</span>
          )}
          {cluster.label}
        </h3>
        <p className="text-xs text-zinc-500 mt-1">
          Signals: {cluster.member_count} · Evidence: {cluster.source_count}
          {cluster.created_at ? ` · ${new Date(cluster.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : ""}
        </p>
        {cluster.top_keywords?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {cluster.top_keywords.slice(0, 3).map((k) => (
              <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600">{k}</span>
            ))}
          </div>
        )}
      </div>
      <div className="text-center flex-shrink-0">
        <div className="text-2xl font-black text-zinc-900 leading-none">{cluster.craving_score}</div>
        <div className={`mt-1 inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${scoreTone(badge)}`}>{badge}</div>
      </div>
    </div>
  );
};

const CommercePage = () => {
  const [clusterData, setClusterData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [marketName, setMarketName] = useState("Pets");
  const [cravingId, setCravingId] = useState(null);
  const [clusterId, setClusterId] = useState(null);
  const [sortBy, setSortBy] = useState("score");
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [shortlistOnly, setShortlistOnly] = useState(false);
  const [viewMode, setViewMode] = useState("explore");
  const [diligenceCid, setDiligenceCid] = useState(null);
  const [insightMap, setInsightMap] = useState({});

  const authHeader = () => {
    const t = localStorage.getItem("p2p_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    axios.get(`${API}/commerce/craving/clusters`)
      .then(({ data }) => {
        if (data?.clusters?.length > 0) {
          setClusterData(data);
          const firstMarket = data.markets[0]?.name;
          setMarketName(firstMarket);
          const first = data.clusters.find((c) => c.market === firstMarket);
          setClusterId(first?.cluster_id);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
    axios.get(`${API}/commerce/shortlist`, { headers: authHeader() })
      .then(({ data }) => setShortlist(data?.cluster_ids || []))
      .catch(() => {});
    axios.get(`${API}/commerce/review/queue`)
      .then(({ data }) => {
        const map = {};
        (data?.items || []).forEach((it) => { map[it.cluster_id] = it; });
        setInsightMap(map);
      })
      .catch(() => {});
  }, []);

  const isReal = !!(clusterData && clusterData.clusters?.length > 0);
  const isShortlisted = (id) => shortlist.includes(id);

  const toggleShortlist = (cluster) => {
    const id = cluster.cluster_id;
    const next = isShortlisted(id) ? shortlist.filter((x) => x !== id) : [...shortlist, id];
    setShortlist(next); // optimistic
    axios.post(`${API}/commerce/shortlist`, {
      cluster_id: id, market: cluster.market,
      cluster_name: cluster.label, craving_score: cluster.craving_score,
    }, { headers: authHeader() })
      .then(({ data }) => {
        setShortlist((cur) => data.shortlisted
          ? Array.from(new Set([...cur, id]))
          : cur.filter((x) => x !== id));
      })
      .catch(() => setShortlist(shortlist)); // revert on failure
  };

  // ---- REAL data derived views ----
  const realMarkets = clusterData?.markets || [];
  const allClusters = clusterData?.clusters || [];

  const toggleFilter = (id) =>
    setFilters((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));

  const realClusters = useMemo(() => {
    let list = marketName === ALL_MARKETS
      ? [...allClusters]
      : allClusters.filter((c) => c.market === marketName);

    if (shortlistOnly) list = list.filter((c) => shortlist.includes(c.cluster_id));

    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const hay = [
          c.label, c.desired_outcome, c.customer_type, c.product_category_hint,
          (c.top_keywords || []).join(" "), (c.sample_pains || []).join(" "),
        ].join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    filters.forEach((fid) => {
      if (fid === "shortlisted") { list = list.filter((c) => shortlist.includes(c.cluster_id)); return; }
      const f = CLUSTER_FILTERS.find((x) => x.id === fid);
      if (f) list = list.filter(f.test);
    });

    const sorter = SORT_OPTIONS.find((s) => s.id === sortBy) || SORT_OPTIONS[0];
    list.sort((a, b) => {
      const av = sorter.key(a), bv = sorter.key(b);
      return av < bv ? 1 : av > bv ? -1 : 0;
    });
    return list;
  }, [allClusters, marketName, query, filters, sortBy, shortlistOnly, shortlist]);

  const selectedCluster = useMemo(
    () => realClusters.find((c) => c.cluster_id === clusterId) || realClusters[0],
    [realClusters, clusterId]
  );

  // Cross-market summary strip (computed from ALL clusters, not the filtered view).
  const summary = useMemo(() => {
    if (!allClusters.length) return null;
    const scores = allClusters.map((c) => c.craving_score || 0);
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    const topMarket = [...realMarkets].sort((a, b) => b.top_score - a.top_score)[0];
    return {
      total: allClusters.length,
      avgScore: Math.round(avg * 10) / 10,
      topMarket: topMarket ? `${topMarket.name} (${topMarket.top_score})` : "—",
      newWeek: allClusters.filter(isNewCluster).length,
      singletons: allClusters.filter((c) => (c.member_count || 0) === 1).length,
      multiSource: allClusters.filter((c) => (c.source_count || 0) >= 2).length,
      shortlisted: shortlist.length,
    };
  }, [allClusters, realMarkets, shortlist]);

  // Per-market "new this week" counts for the market picker.
  const newByMarket = useMemo(() => {
    const map = {};
    allClusters.forEach((c) => {
      if (isNewCluster(c)) map[c.market || "Unknown"] = (map[c.market || "Unknown"] || 0) + 1;
    });
    return map;
  }, [allClusters]);

  // ---- MOCK fallback views ----
  const cravings = CRAVINGS[marketName] || [];
  const selectedCraving = useMemo(
    () => cravings.find((c) => c.id === cravingId) || cravings[0],
    [cravings, cravingId]
  );

  const selectMarket = (name) => {
    setMarketName(name);
    if (isReal) {
      const first = name === ALL_MARKETS
        ? allClusters[0]
        : allClusters.find((c) => c.market === name);
      setClusterId(first?.cluster_id);
    } else {
      setCravingId((CRAVINGS[name] || [])[0]?.id);
    }
  };

  const heroStats = isReal
    ? [
        { value: clusterData.total_clusters, label: "Validated Opportunities" },
        { value: clusterData.total_cravings, label: "Customer Signals Collected" },
        { value: realMarkets.length, label: "Markets Covered" },
        { value: "3", label: "Data Sources" },
        {
          value: (() => {
            const cs = allClusters.map((c) => c.metrics?.avg_confidence).filter((x) => x != null);
            return cs.length ? `${Math.round((cs.reduce((a, b) => a + b, 0) / cs.length) * 100)}%` : "—";
          })(),
          label: "Average AI Confidence",
        },
        { value: "Demand Validation Complete", label: "Pipeline Stage" },
      ]
    : HERO_STATS;

  const timeMachine = !isReal
    ? (selectedCraving?.detail || buildGenericDetail(selectedCraving || {}, marketName)).timeMachine
    : null;

  return (
    <div className="min-h-screen" data-testid="commerce-page"
      style={{ background: "radial-gradient(circle at top left, rgba(148,99,255,0.12), transparent 34%), linear-gradient(180deg,#f8f6ff 0%,#ffffff 46%,#f7f5ff 100%)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">

        {/* 1. HERO */}
        <section className="text-center max-w-3xl mx-auto" data-testid="commerce-hero">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-violet-100 shadow-sm text-sm font-bold text-violet-700">
            <Sparkle size={15} weight="fill" /> Commerce Intelligence
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-zinc-900 leading-[1.02]">
            Find tomorrow’s products{" "}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg,#7c4dff,#b253ff)" }}>before they become trends.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-500 leading-relaxed">
            PrimePillar scans real customer conversations, reviews and searches to uncover what people
            are starting to crave — grouped into semantic opportunity clusters.
          </p>
          {isReal && (
            <div className="mt-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-xs font-bold text-emerald-700" data-testid="commerce-real-label">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Real Craving Clusters · Live data from customer signals
            </div>
          )}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#commerce-markets"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:opacity-90 shadow-[0_14px_30px_-8px_rgba(139,92,246,0.7)]"
              style={{ background: "linear-gradient(90deg,#7c4dff,#b253ff)" }} data-testid="commerce-cta-explore">
              Explore Demand <ArrowRight size={16} weight="bold" />
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {heroStats.map((s, i) => (
              <div key={`${s.label}-${i}`} className="rounded-2xl bg-white/75 backdrop-blur border border-violet-100 px-3 py-4 shadow-sm">
                <div className="text-xl font-black text-zinc-900">{s.value}</div>
                <div className="text-[11px] text-zinc-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Loading / empty states — real data only; mock never masquerades as real */}
        {!loaded && (
          <div className="mt-16 text-center text-zinc-400 text-sm" data-testid="commerce-loading">
            Loading real craving clusters…
          </div>
        )}
        {loaded && !isReal && (
          <section className="mt-16" data-testid="commerce-empty-state">
            <div className="max-w-xl mx-auto rounded-2xl border border-dashed border-violet-200 bg-white/70 p-8 text-center">
              <h3 className="text-lg font-bold text-zinc-900">No craving clusters in this environment</h3>
              <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
                The Commerce pipeline data (customer craving clusters, demand validation & AI review) lives in the
                environment where it was generated. If you're on <b>production</b>, redeploy and run the pipeline there
                (or open the <b>preview</b> build). This page populates automatically once real clusters are available —
                no mock data is shown.
              </p>
            </div>
          </section>
        )}

        {/* MODE TOGGLE: Explore vs Quality Review (Phase 4.5 gate) */}
        {isReal && (
          <div className="mt-12 flex lg:justify-center overflow-x-auto pb-1 -mx-3 px-3 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="commerce-mode-toggle">
            <div className="inline-flex flex-nowrap flex-shrink-0 rounded-full bg-white/80 backdrop-blur border border-violet-100 p-1 shadow-sm">
              <button onClick={() => setViewMode("explore")}
                className={`text-sm font-bold px-5 py-2 rounded-full transition-all whitespace-nowrap ${viewMode === "explore" ? "bg-violet-600 text-white shadow" : "text-zinc-600 hover:text-violet-600"}`}
                data-testid="commerce-mode-explore">
                Explore Clusters
              </button>
              <button onClick={() => setViewMode("review")}
                className={`text-sm font-bold px-5 py-2 rounded-full transition-all whitespace-nowrap ${viewMode === "review" ? "bg-violet-600 text-white shadow" : "text-zinc-600 hover:text-violet-600"}`}
                data-testid="commerce-mode-review">
                Quality Review
              </button>
              <button onClick={() => setViewMode("workspace")}
                className={`text-sm font-bold px-5 py-2 rounded-full transition-all whitespace-nowrap ${viewMode === "workspace" ? "bg-violet-600 text-white shadow" : "text-zinc-600 hover:text-violet-600"}`}
                data-testid="commerce-mode-workspace">
                Review Workspace
              </button>
              <button onClick={() => setViewMode("health")}
                className={`text-sm font-bold px-5 py-2 rounded-full transition-all whitespace-nowrap ${viewMode === "health" ? "bg-violet-600 text-white shadow" : "text-zinc-600 hover:text-violet-600"}`}
                data-testid="commerce-mode-health">
                Pipeline Health
              </button>
              <button onClick={() => setViewMode("validation")}
                className={`text-sm font-bold px-5 py-2 rounded-full transition-all whitespace-nowrap ${viewMode === "validation" ? "bg-violet-600 text-white shadow" : "text-zinc-600 hover:text-violet-600"}`}
                data-testid="commerce-mode-validation">
                Prediction Validation
              </button>
              <button onClick={() => setViewMode("diligence")}
                className={`text-sm font-bold px-5 py-2 rounded-full transition-all whitespace-nowrap ${viewMode === "diligence" ? "bg-violet-600 text-white shadow" : "text-zinc-600 hover:text-violet-600"}`}
                data-testid="commerce-mode-diligence">
                Due Diligence
              </button>
              <button onClick={() => setViewMode("decisions")}
                className={`text-sm font-bold px-5 py-2 rounded-full transition-all whitespace-nowrap ${viewMode === "decisions" ? "bg-violet-600 text-white shadow" : "text-zinc-600 hover:text-violet-600"}`}
                data-testid="commerce-mode-decisions">
                Decision Board
              </button>
              <button onClick={() => setViewMode("solutions")}
                className={`text-sm font-bold px-5 py-2 rounded-full transition-all whitespace-nowrap ${viewMode === "solutions" ? "bg-violet-600 text-white shadow" : "text-zinc-600 hover:text-violet-600"}`}
                data-testid="commerce-mode-solutions">
                Existing Solutions
              </button>
            </div>
          </div>
        )}

        {isReal && viewMode === "solutions" && (
          <section className="mt-8" data-testid="commerce-solutions-section">
            <SectionHead step="Phase 5" title="Existing Solutions" sub="Who already sells this? Raw marketplace supply (image, price, rating, review count) for opportunities you've marked Build. Runs only on approved clusters — no gaps, reviews or strategy here." />
            <ExistingSolutions />
          </section>
        )}

        {isReal && viewMode === "diligence" && (
          <section className="mt-8" data-testid="commerce-diligence-section">
            <SectionHead step="Verification" title="Founder Due Diligence" sub="Investor-grade, evidence-backed, fully auditable. Every claim links to the original source — verify each assumption before Phase 5 or real spend." />
            <FounderDiligence initialCid={diligenceCid} />
          </section>
        )}

        {isReal && viewMode === "decisions" && (
          <section className="mt-8" data-testid="commerce-decisions-section">
            <SectionHead step="Phase 12" title="Decision Board — where should the capital go?" sub="Every generated Decision Report ranked by overall score. Scores come straight from the locked phases — compare opportunities side by side, then open the full report." />
            <DecisionBoard onOpen={(cid) => { setDiligenceCid(cid); setViewMode("diligence"); }} />
          </section>
        )}

        {isReal && viewMode === "validation" && (
          <section className="mt-8" data-testid="commerce-validation-section">
            <SectionHead step="Validation" title="Prediction Validation" sub="Was the engine actually right? Calibration, funnel, and reliability from REAL outcomes only — never estimated. Populates as the pipeline runs." />
            <PredictionValidation />
          </section>
        )}

        {isReal && viewMode === "health" && (
          <section className="mt-8" data-testid="commerce-health-section">
            <SectionHead step="Operations" title="Pipeline Health" sub="Engine quality at a glance. Every improvement to the pipeline should move these numbers. Decision metrics populate as you review." />
            <PipelineHealth />
          </section>
        )}

        {isReal && viewMode === "workspace" && (
          <section className="mt-8" data-testid="commerce-workspace-section">
            <SectionHead step="Validation" title="Review Workspace — Build / Watch / Reject" sub="Everything needed to decide, in one place. Each decision is captured permanently as training data. No Phase 5 spend until you approve." />
            <ReviewWorkspace />
          </section>
        )}

        {isReal && viewMode === "review" && (
          <section className="mt-8" data-testid="commerce-review-section">
            <SectionHead step="Quality Gate" title="Commercial review before Phase 5" sub="Approve, defer or reject each cluster. Rejected clusters are excluded from all downstream phases." />
            <QualityReview />
          </section>
        )}

        {isReal && viewMode === "explore" && (
        <>
        {/* 2. MARKET SELECTOR */}
        <section id="commerce-markets" className="mt-16 scroll-mt-24" data-testid="commerce-market-selector">
          <SectionHead step="Step 1" title="Pick a market to explore" sub="Start with where customers are complaining, not with products." />

          {/* Cross-market summary strip */}
          {isReal && summary && (
            <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="commerce-summary-strip">
              {[
                ["Total Opportunities", summary.total],
                ["New This Week", summary.newWeek, true],
                ["Avg Opportunity Score", summary.avgScore],
                ["Top Market", summary.topMarket],
                ["Singletons", summary.singletons],
                ["Multi-evidence", summary.multiSource],
                ["Shortlisted", summary.shortlisted],
              ].map(([label, value, hot]) => (
                <div key={label} className={`rounded-xl backdrop-blur border px-3.5 py-3 shadow-sm ${hot ? "bg-emerald-50/90 border-emerald-200" : "bg-white/80 border-violet-100"}`}
                  data-testid={hot ? "commerce-stat-new-week" : undefined}>
                  <div className={`text-lg font-black leading-none truncate ${hot ? "text-emerald-700" : "text-zinc-900"}`}>{value}</div>
                  <div className={`text-[10px] mt-1 ${hot ? "text-emerald-600 font-semibold" : "text-zinc-500"}`}>{label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {isReal ? (
              <>
                <button onClick={() => selectMarket(ALL_MARKETS)}
                  className={`text-left rounded-2xl p-5 backdrop-blur border transition-all hover:-translate-y-0.5 ${marketName === ALL_MARKETS ? "border-violet-400 ring-2 ring-violet-200 shadow-[0_20px_50px_-20px_rgba(139,92,246,0.5)]" : "border-violet-100 hover:border-violet-300 shadow-sm"}`}
                  style={{ background: marketName === ALL_MARKETS ? "linear-gradient(135deg,rgba(139,92,246,0.10),rgba(168,85,247,0.06))" : "rgba(255,255,255,0.75)" }}
                  data-testid="commerce-market-all">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                      style={{ background: "linear-gradient(135deg,#7c4dff,#b253ff)" }}>
                      <Storefront size={18} weight="fill" />
                    </span>
                    <h3 className="text-lg font-bold text-zinc-900">All Markets</h3>
                  </div>
                  <p className="text-sm text-zinc-500">{clusterData.total_clusters} clusters · {clusterData.total_cravings} cravings</p>
                  <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-violet-600">
                    <TrendUp size={13} weight="bold" /> browse everything
                  </div>
                </button>
                {realMarkets.map((m) => (
                  <button key={m.name} onClick={() => selectMarket(m.name)}
                    className={`text-left rounded-2xl p-5 bg-white/75 backdrop-blur border transition-all hover:-translate-y-0.5 ${m.name === marketName ? "border-violet-400 ring-2 ring-violet-200 shadow-[0_20px_50px_-20px_rgba(139,92,246,0.5)]" : "border-violet-100 hover:border-violet-300 shadow-sm"}`}
                    data-testid={`commerce-market-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black text-white flex-shrink-0"
                        style={{ background: "linear-gradient(135deg,#8b5cf6,#a855f7)" }}>{m.name[0]}</span>
                      <h3 className="text-lg font-bold text-zinc-900">{m.name}</h3>
                    </div>
                    <p className="text-sm text-zinc-500">{m.cluster_count} clusters · {m.craving_total} cravings</p>
                    {newByMarket[m.name] > 0 && (
                      <span className="mt-1.5 mr-2 inline-flex items-center gap-1 text-[10px] font-black uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200" data-testid={`market-new-${m.name.toLowerCase()}`}>
                        +{newByMarket[m.name]} new this week
                      </span>
                    )}
                    <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-emerald-600">
                      <TrendUp size={13} weight="bold" /> top score {m.top_score}
                    </div>
                  </button>
                ))}
              </>
            ) : (
              MARKETS.map((m) => (
                <MarketCard key={m.name} market={m} active={m.name === marketName} onClick={() => selectMarket(m.name)} />
              ))
            )}
          </div>
        </section>

        {/* 3 + 4. CRAVINGS + DETAIL */}
        <section id="commerce-opportunity" className="mt-16 grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)] gap-6 items-start scroll-mt-24">
          <div>
            <SectionHead step="Step 2"
              title={isReal ? (marketName === ALL_MARKETS ? "All customer opportunities" : `${marketName} customer opportunities`) : `${marketName} customer cravings`}
              sub={isReal ? "Browse, search, sort and filter validated customer opportunities." : "Ranked by pain, growth, purchase intent, and competition gap."} />

            {isReal && (
              <div className="mb-4 space-y-3" data-testid="commerce-cluster-controls">
                {/* Search */}
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search cravings across all markets…"
                    className="w-full rounded-xl border border-violet-100 bg-white/85 pl-9 pr-9 py-2.5 text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                    data-testid="commerce-cluster-search"
                  />
                  {query && (
                    <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" data-testid="commerce-cluster-search-clear">
                      <X size={15} weight="bold" />
                    </button>
                  )}
                </div>
                {/* Sort toggle */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-zinc-400 mr-1">Sort</span>
                  {SORT_OPTIONS.map((s) => (
                    <button key={s.id} onClick={() => setSortBy(s.id)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${sortBy === s.id ? "bg-violet-600 text-white shadow-sm" : "bg-white text-zinc-600 border border-zinc-200 hover:border-violet-300"}`}
                      data-testid={`commerce-sort-${s.id}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                {/* Filter chips */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-zinc-400 mr-1">Filter</span>
                  {CLUSTER_FILTERS.map((f) => (
                    <button key={f.id} onClick={() => toggleFilter(f.id)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${filters.includes(f.id) ? "bg-emerald-600 text-white shadow-sm" : "bg-white text-zinc-600 border border-zinc-200 hover:border-emerald-300"}`}
                      data-testid={`commerce-filter-${f.id}`}>
                      {f.label}
                    </button>
                  ))}
                  <button onClick={() => toggleFilter("shortlisted")}
                    className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full transition-all ${filters.includes("shortlisted") ? "bg-amber-500 text-white shadow-sm" : "bg-white text-zinc-600 border border-zinc-200 hover:border-amber-300"}`}
                    data-testid="commerce-filter-shortlisted">
                    <Star size={12} weight={filters.includes("shortlisted") ? "fill" : "regular"} /> Shortlisted
                  </button>
                  {filters.length > 0 && (
                    <button onClick={() => setFilters([])} className="text-xs font-semibold text-zinc-400 hover:text-zinc-600 px-2" data-testid="commerce-filter-clear">
                      Clear
                    </button>
                  )}
                </div>
                {/* Shortlisted-only view toggle */}
                <div className="flex items-center justify-between">
                  <button onClick={() => setShortlistOnly((v) => !v)}
                    className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all ${shortlistOnly ? "bg-amber-500 text-white shadow-sm" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100"}`}
                    data-testid="commerce-shortlist-only-toggle">
                    <Star size={13} weight="fill" /> {shortlistOnly ? "Showing shortlisted only" : "Shortlisted only"} ({shortlist.length})
                  </button>
                </div>
                <p className="text-[11px] text-zinc-400" data-testid="commerce-cluster-count">
                  Showing {realClusters.length} of {allClusters.length} opportunities
                </p>
              </div>
            )}

            <div className="space-y-3" data-testid="commerce-craving-list">
              {isReal
                ? (realClusters.length > 0
                    ? realClusters.map((cl) => (
                        <RealCravingCard key={cl.cluster_id} cluster={cl} showMarket={marketName === ALL_MARKETS}
                          shortlisted={isShortlisted(cl.cluster_id)} onToggleShortlist={toggleShortlist}
                          active={cl.cluster_id === selectedCluster?.cluster_id} onClick={() => setClusterId(cl.cluster_id)} />
                      ))
                    : <div className="rounded-2xl border border-dashed border-violet-200 bg-white/60 p-8 text-center text-sm text-zinc-500" data-testid="commerce-cluster-empty">
                        {shortlistOnly || filters.includes("shortlisted") ? "No shortlisted clusters yet — tap the ⭐ on a cluster to add it." : "No clusters match your search or filters."}
                      </div>)
                : cravings.map((cr) => (
                    <CravingCard key={cr.id} craving={cr} active={cr.id === selectedCraving?.id} onClick={() => setCravingId(cr.id)} />
                  ))}
            </div>
          </div>
          {isReal
            ? <RealClusterDetail cluster={selectedCluster} insight={insightMap[selectedCluster?.cluster_id]} />
            : (selectedCraving && <OpportunityPanel craving={selectedCraving} marketName={marketName} />)}
        </section>

        {/* MOCK-ONLY sections — hidden in real mode (Phase 3 boundary: no trends / launch). */}
        {!isReal && (
          <>
            <section className="mt-16" data-testid="commerce-time-machine">
              <SectionHead step="Step 3" title="Time Machine — where is this market going?" sub="See where the market came from, where it is now, and where it may go next." />
              <div className="grid md:grid-cols-3 gap-4">
                {timeMachine.map((t, i) => (
                  <div key={i} className="rounded-2xl bg-white/75 backdrop-blur border border-violet-100 p-5 shadow-sm">
                    <div className="flex items-center gap-2 text-violet-600 font-bold text-sm">
                      <Clock size={15} weight="duotone" /> {t.title}
                    </div>
                    <p className="mt-2.5 text-sm text-zinc-600 leading-relaxed">{t.text}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16" data-testid="commerce-test-tracker">
              <SectionHead step="Step 4" title="Test Tracker Preview" sub="Every launch teaches the engine what works and what fails." />
              <div className="rounded-2xl bg-white/80 backdrop-blur border border-violet-100 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-zinc-500 border-b border-zinc-100">
                      {["Product", "Score", "Spend", "CTR", "CPA", "ROAS", "Profit", "Decision"].map((h) => (
                        <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TEST_TRACKER.map((r) => (
                      <tr key={r.product} className="border-b border-zinc-50 last:border-0" data-testid={`commerce-tracker-row-${r.product.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                        <td className="px-4 py-3 font-semibold text-zinc-800 whitespace-nowrap">{r.product}</td>
                        <td className="px-4 py-3 text-zinc-600">{r.score}</td>
                        <td className="px-4 py-3 text-zinc-600">{r.spend}</td>
                        <td className="px-4 py-3 text-zinc-600">{r.ctr}</td>
                        <td className="px-4 py-3 text-zinc-600">{r.cpa}</td>
                        <td className="px-4 py-3 text-zinc-600">{r.roas}</td>
                        <td className={`px-4 py-3 font-bold ${r.good ? "text-emerald-600" : "text-rose-600"}`}>{r.profit}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${r.good ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{r.decision}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-[11px] text-zinc-400 flex items-center gap-1.5">
                <ChartLineUp size={13} /> Sample intelligence — this is a frontend prototype using mock data. Live scanning connects later.
              </p>
            </section>
          </>
        )}

        {isReal && (
          <p className="mt-12 text-center text-[11px] text-zinc-400 flex items-center justify-center gap-1.5" data-testid="commerce-phase-note">
            <Storefront size={13} /> Phase 4.5 — Cravings, demand validation &amp; quality review. Solutions, manufacturers &amp; launch tools connect in later phases.
          </p>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default CommercePage;
